import { Role, resolveRole } from '../../utils/permissions';
import { isUnlinkedInvite } from '../../utils/preferredCompany';

export type PmRecipientCandidate = {
    email?: string | null;
    name?: string | null;
    role?: string | null;
    cognitoId?: string | null;
};

const INCLUDED_ROLES = new Set<Role>([Role.SUPER_ADMIN, Role.ADMIN, Role.INSPECTOR]);

/** Admin and inspector accounts. Operators, foremen, and service technicians are left out. */
export function isPmNotificationRecipient(user: PmRecipientCandidate): boolean {
    const email = user.email?.trim();
    if (!email) return false;
    const role = resolveRole(user.role);
    if (!role || !INCLUDED_ROLES.has(role)) return false;
    if (role !== Role.SUPER_ADMIN && isUnlinkedInvite(user.cognitoId)) return false;
    return true;
}

export function pmRecipientEmail(user: PmRecipientCandidate): string {
    return (user.email || '').trim().toLowerCase();
}

export function pmRecipientOptionLabel(user: PmRecipientCandidate): string {
    const email = pmRecipientEmail(user);
    const name = user.name?.trim();
    return name ? `${name} (${email})` : email;
}

export function pmRecipientOptions(
    users: PmRecipientCandidate[],
    selectedEmails: string[] = [],
): { value: string; label: string }[] {
    const byEmail = new Map<string, { value: string; label: string }>();

    for (const user of users) {
        if (!isPmNotificationRecipient(user)) continue;
        const email = pmRecipientEmail(user);
        byEmail.set(email, { value: email, label: pmRecipientOptionLabel(user) });
    }

    for (const raw of selectedEmails) {
        const email = raw.trim().toLowerCase();
        if (!email || byEmail.has(email)) continue;
        byEmail.set(email, { value: email, label: email });
    }

    return [...byEmail.values()].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
}

export function pmRecipientDisplay(email: string, users: PmRecipientCandidate[]): string {
    const key = email.trim().toLowerCase();
    const match = users.find((user) => isPmNotificationRecipient(user) && pmRecipientEmail(user) === key);
    return match ? pmRecipientOptionLabel(match) : email.trim();
}
